import { useState, useEffect } from "react";

const subscribers = new Map<
  string,
  Set<React.Dispatch<React.SetStateAction<unknown>>>
>();

/**
 * useLocalStorage actively notifies all the subscribes about the local storage value change.
 * When two different components use the same key, it will notify both of them.
 */
const useLocalStorage = <T>(
  key: string,
  initialValue?: T,
  serializer = JSON.stringify,
  deserializer = JSON.parse,
) => {
  const [state, setState] = useState<T | undefined | null>(() => {
    const current = getLocalStorageItem(key);

    if (current) {
      try {
        return deserializer(current);
      } catch {
        removeLocalStorageItem(key);
      }
    }

    return initialValue;
  });

  const setAndNotify = (
    newValue: React.SetStateAction<T | undefined | null>,
  ) => {
    setState((prev) => {
      const resolved =
        typeof newValue === "function"
          ? (newValue as (prev: T | undefined | null) => T | undefined | null)(
              prev,
            )
          : newValue;

      if (resolved === null || resolved === undefined) {
        removeLocalStorageItem(key);
      } else {
        setLocalStorageItem(key, resolved, serializer);
      }

      const subs = subscribers.get(key);
      if (subs) {
        queueMicrotask(() => subs.forEach((fn) => fn(resolved)));
      }

      return resolved;
    });
  };

  useEffect(() => {
    let subs = subscribers.get(key);
    if (!subs) {
      subs = new Set();
      subscribers.set(key, subs);
    }
    subs.add(setState);

    return () => {
      subs!.delete(setState);
      if (subs!.size === 0) subscribers.delete(key);
    };
  }, [key]);

  const deleteState = () => {
    setAndNotify(undefined);
  };

  return [
    state as typeof initialValue extends undefined ? T | null : T,
    setAndNotify,
    deleteState,
  ] as const;
};

const setLocalStorageItem = (
  key: string,
  value: unknown,
  serializer = JSON.stringify,
) => {
  const stringified = serializer(value);
  window.localStorage.setItem(key, stringified);
};

const removeLocalStorageItem = (key: string) => {
  window.localStorage.removeItem(key);
};

const getLocalStorageItem = (key: string) => window.localStorage.getItem(key);

export { useLocalStorage };
